import { disableMapKvp$, season$, urlSearchParamsChanged$, } from '../stores/store-main.js';
import { getLayerTreeCodes } from '../layers/layerHelpers.js';
import { bgLayer$, visibleAdditionalRouteLayers$, visibleLayers$, } from '../stores/store-map.js';
import { getCodeForAnyLang, landUrlFragment } from './urlParams.js';
import { IMAGE_BASE_URL, OIDC_LOGIN } from '../envs.js';
import { getInitialSearchParams } from './initialSearchParams.js';
export function getURLSearchParams() {
    if (typeof window === 'undefined')
        return new URLSearchParams();
    return new URLSearchParams(location.search);
}
export function isDetoursEnabled() {
    const detours = getURLSearchParams().get('detours');
    return !detours || detours === 'yes';
}
export function isEmbed() {
    return location?.search.includes('embed');
}
export function isShootingEnabled() {
    const shooting = getURLSearchParams().get('shooting');
    return !shooting || shooting === 'yes';
}
export function isPhotosEnabled() {
    const photos = getURLSearchParams().get('photos');
    return !photos || photos === 'yes';
}
export function isLogosEnabled() {
    const logos = getURLSearchParams().get('logos');
    return !logos || logos === 'yes';
}
export function extendedZoom() {
    const active = getURLSearchParams().get('extendedZoom');
    return active === 'yes';
}
export const FILTER_PARAMS = [
    'f_land',
    'f_categories',
    'f_grades',
    'f_gradesSkiing',
    'f_fitness',
    'f_canton',
    'f_bookable',
    'f_day',
    'f_group',
    'f_structures',
];
export function getLayersForLayertree(searchParams) {
    const params = searchParams || getURLSearchParams();
    const season = params.get('season');
    const layers = params.get('layers') === ''
        ? []
        : params.get('layers')?.toLowerCase().split(',');
    if (!layers)
        return [];
    if (layers.includes('accomodation')) {
        layers.splice(layers.findIndex((v) => v === 'accomodation'), 1, 'accommodation');
    }
    if (layers.includes('wildlifearea') || layers.includes('wildlifereserve')) {
        const areaIndex = layers.findIndex((v) => v === 'wildlifearea');
        const reserveIndex = layers.findIndex((v) => v === 'wildlifereserve');
        if (areaIndex > -1)
            delete layers[areaIndex];
        if (reserveIndex > -1)
            delete layers[reserveIndex];
        layers.push('wildlife');
    }
    if (layers.includes('services')) {
        const winterServices = ['place', 'sightseeing'];
        const summerServices = [...winterServices, 'velorental', 'cycleservice'];
        const services = season === 'winter' ? winterServices : summerServices;
        layers.splice(layers.findIndex((v) => v === 'services'), 1, ...services);
    }
    return getLayerTreeCodes(layers);
}
export function getMapKvp(view) {
    const params = getURLSearchParams();
    const center = view.getCenter();
    const resolution = view.getResolution();
    if (center) {
        params.set('E', center[0].toFixed(0));
        params.set('N', center[1].toFixed(0));
    }
    if (resolution)
        params.set('resolution', resolution.toFixed(2));
    params.set('bgLayer', bgLayer$.getValue());
    const layers = visibleLayers$.getValue();
    params.set('layers', `${layers.join(',')}`);
    if (!params.get('detours') || params.get('detours') !== 'no') {
        params.set('detours', visibleAdditionalRouteLayers$.getValue().detours ? 'yes' : 'no');
    }
    if (!params.get('shooting') || params.get('shooting') !== 'no') {
        params.set('shooting', visibleAdditionalRouteLayers$.getValue().shooting ? 'yes' : 'no');
    }
    else if (params.has('shooting')) {
        params.delete('shooting');
    }
    if (!params.get('logos') || params.get('logos') !== 'no') {
        params.set('logos', visibleAdditionalRouteLayers$.getValue().logos ? 'yes' : 'no');
    }
    if (!params.get('photos') || params.get('photos') !== 'no') {
        params.set('photos', visibleAdditionalRouteLayers$.getValue().photos ? 'yes' : 'no');
    }
    if (!params.get('season')) {
        params.set('season', season$.getValue());
    }
    return params;
}
export function crosscountryBaseNetworkShown() {
    const params = getURLSearchParams();
    const baseNetworkParam = params.get('crosscountryBaseNetwork');
    return !!(baseNetworkParam && baseNetworkParam === 'true');
}
export function setURLSearchParams(params, force = false) {
    if (disableMapKvp$.getValue() && !force)
        return;
    window.history.replaceState({}, '', `${location.pathname}${params.toString() ? `?${params}` : ''}`);
    urlSearchParamsChanged$.next(params);
}
export function getURLSearchParam(key) {
    const params = getURLSearchParams();
    return params.get(key);
}
export function setURLSearchParam(key, value) {
    const params = getURLSearchParams();
    params.set(key, value);
    setURLSearchParams(params);
}
export function getMapResolution() {
    const params = getURLSearchParams();
    return Number(params.get('resolution'));
}
export function getLandCodeFromUrl() {
    const splitUrl = location.pathname.split('/');
    return getCodeForAnyLang(splitUrl[2], landUrlFragment);
}
export function getOidcLoginEnabled() {
    if (OIDC_LOGIN === '1') {
        return true;
    }
    const loginEnabled = getInitialSearchParams().get('loginEnabled');
    return loginEnabled === 'yes' || loginEnabled === 'true';
}
export function hasFilterUrlParam() {
    const filtersUrlParams = getURLSearchParams();
    const hasFilterParams = FILTER_PARAMS.some((filter) => filtersUrlParams.has(filter));
    const hasOldFilterParams = !!filtersUrlParams.get('filters');
    return hasFilterParams || hasOldFilterParams;
}
export const getPhotoUrl = (code, status) => {
    return `${IMAGE_BASE_URL}${status}/${code}_big.jpg`;
};
export function toursFeedbackParamCheck() {
    const params = getURLSearchParams();
    const hasFeedbackParam = params.has('toursFeedback');
    if (hasFeedbackParam) {
        params.delete('toursFeedback');
        setURLSearchParams(params, true);
    }
    return hasFeedbackParam;
}
export function getRedirectParam() {
    const redirect = getURLSearchParams().get('redirect');
    return redirect ? decodeURIComponent(redirect) : redirect;
}
const fileIdParam = 'fileId';
export function setFileId(fileId) {
    const params = new URLSearchParams();
    params.set(fileIdParam, fileId);
    return params;
}
export function setFileTitleParam(fileTitle) {
    const params = new URLSearchParams();
    params.set('fileTitle', fileTitle);
    return params;
}
export function getFileTitle() {
    const params = getURLSearchParams();
    return params.get('fileTitle') || '';
}
export function deleteFileTitleParam() {
    const params = new URLSearchParams();
    params.delete('fileTitle');
    setURLSearchParams(params, true);
    return params;
}
export function getFileId() {
    const params = getURLSearchParams();
    return params.get(fileIdParam) || '';
}
export function isAnimationEnabled() {
    const animationEnabled = getURLSearchParams().get('animation');
    return animationEnabled === 'yes' || animationEnabled === 'true';
}
export const isNewSearchEnabled = getURLSearchParams().get('newsearch') !== null;
export const isLayersStoringEnabled = getURLSearchParams().get('storelayers') !== null;
export const isSubscriptionExpiredEnabled = getURLSearchParams().get('subscriptionExpired') !== null;
export const isDetourHintEnabled = getURLSearchParams().get('detourHint') !== null;
export const isSummerNetworksEnabled = getURLSearchParams().get('summernetworks') !== null;
export const isContentDbEnabled = getURLSearchParams().get('usecontentdb') !== null;
export function isTestRouteStylesEnabled() {
    const testRouteStyles = getURLSearchParams().get('testRouteStyles');
    return (typeof testRouteStyles === 'string' &&
        testRouteStyles !== 'false' &&
        testRouteStyles !== 'no');
}
const activityNetworkPrefixes = {
    velonetwork: 'velo',
    mtbnetwork: 'mtb',
    skatenetwork: 'skate',
};
function getActivityNetworkValues(build) {
    return {
        velonetwork: build('velo'),
        mtbnetwork: build('mtb'),
        skatenetwork: build('skate'),
    };
}
export function getTestRouteStyles() {
    const params = getURLSearchParams();
    const routesWidth = params
        .get('summer_routes_width')
        ?.split(',')
        .map((v) => Number(v) || 1) || [14, 12, 7];
    const routesOpacity = {
        wanderland: Number(params.get('hike_route_opacity')) || 0.6,
        veloland: Number(params.get('velo_route_opacity')) || 0.6,
        mtbland: Number(params.get('mtb_route_opacity')) || 0.6,
        skatingland: Number(params.get('skating_route_opacity')) || 0.6,
        kanuland: Number(params.get('canoe_route_opacity')) || 0.6,
    };
    const innerRouteColors = {
        wanderland: params.get('hike_route_i_color') || '#00A800',
        veloland: params.get('velo_route_i_color') || '#0099FF',
        mtbland: params.get('mtb_route_i_color') || '#CC6600',
        skatingland: params.get('skating_route_i_color') || '#875BCC',
        kanuland: params.get('canoe_route_i_color') || '#008770',
    };
    const outerRouteColors = {
        veloland: params.get('velo_route_o_color') || '#0033FF',
        mtbland: params.get('mtb_route_o_color') || '#663300',
        skatingland: params.get('skating_route_o_color') || '#5C349A',
        kanuland: params.get('canoe_route_o_color') || '#175A50',
    };
    const networksWidth = (params
        .get('summer_network_width')
        ?.split(',')
        .map((v) => {
        const n = Number(v);
        return isNaN(n) ? 1 : n;
    }) ?? [5, 4, 3, 0]);
    const networksOpacity = {
        wanderwegnetz: Number(params.get('hike_net_opacity')) || 0.7,
        velonetwork: Number(params.get('velo_net_opacity')) || 0.7,
        mtbnetwork: Number(params.get('mtb_net_opacity')) || 0.7,
        skatenetwork: Number(params.get('skate_net_opacity')) || 0.7,
    };
    const networksColor = {
        wanderwegnetz: params.get('hike_net_color') || '#609E26',
        velonetwork: params.get('velo_net_color') || '#5C94CF',
        mtbnetwork: params.get('mtb_net_color') || '#DEAB18',
        skatenetwork: params.get('skate_net_color') || '#9D7FB7',
    };
    const networkSurfaceColors = getActivityNetworkValues((prefix) => ({
        paved: params.get(`${prefix}_surf_paved`) || '#808080',
        natural: params.get(`${prefix}_surf_natural`) || '#8B4513',
    }));
    const networkSurfaceOpacity = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_surf_opacity`)) || 1);
    const networkSurfaceDashColor = getActivityNetworkValues((prefix) => params.get(`${prefix}_surf_dash_color`) || '#000000');
    const networkSurfaceDashOpacity = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_surf_dash_opacity`)) || 0);
    const networkSurfaceDashLength = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_surf_dash_len`)) || 8);
    const networkSurfaceDashSpacing = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_surf_dash_gap`)) || 8);
    const networkGradeColors = getActivityNetworkValues((prefix) => ({
        1: params.get(`${prefix}_grade_1`) || '#00C000',
        2: params.get(`${prefix}_grade_2`) || '#0080FF',
        3: params.get(`${prefix}_grade_3`) || '#FFD700',
        4: params.get(`${prefix}_grade_4`) || '#FF8C00',
        5: params.get(`${prefix}_grade_5`) || '#FF0000',
        unknown: params.get(`${prefix}_grade_unknown`) || '#808080',
    }));
    const networkGradeOpacity = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_grade_opacity`)) || 1);
    const networkGradeDashColor = getActivityNetworkValues((prefix) => params.get(`${prefix}_grade_dash_color`) || '#000000');
    const networkGradeDashOpacity = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_grade_dash_opacity`)) || 0);
    const networkGradeDashLength = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_grade_dash_len`)) || 8);
    const networkGradeDashSpacing = getActivityNetworkValues((prefix) => Number(params.get(`${prefix}_grade_dash_gap`)) || 8);
    const dashOpacity = {
        wanderwegnetz: Number(params.get('hike_dash_opacity')) || 0.6,
        velonetwork: Number(params.get('velo_dash_opacity')) || 0.6,
        mtbnetwork: Number(params.get('mtb_dash_opacity')) || 0.6,
        skatenetwork: Number(params.get('skate_dash_opacity')) || 0.6,
    };
    const dashColor = {
        wanderwegnetz: params.get('hike_dash_color') || '#FF6600',
        velonetwork: params.get('velo_dash_color') || '#FF6600',
        mtbnetwork: params.get('mtb_dash_color') || '#FF6600',
        skatenetwork: params.get('skate_dash_color') || '#FF6600',
    };
    const dashLength = {
        wanderwegnetz: Number(params.get('hike_dash_len')) || 8,
        velonetwork: Number(params.get('velo_dash_len')) || 8,
        mtbnetwork: Number(params.get('mtb_dash_len')) || 8,
        skatenetwork: Number(params.get('skate_dash_len')) || 8,
    };
    const dashSpacing = {
        wanderwegnetz: Number(params.get('hike_dash_gap')) || 8,
        velonetwork: Number(params.get('velo_dash_gap')) || 8,
        mtbnetwork: Number(params.get('mtb_dash_gap')) || 8,
        skatenetwork: Number(params.get('skate_dash_gap')) || 8,
    };
    const wanderwegnetzGradeColors = {
        alpine_trail: params.get('hike_alpine_color') || '#0000FF',
        mountain_trail: params.get('hike_mountain_color') || '#FF0000',
        hiking_trail: params.get('hike_hiking_color') || '#FDC200',
    };
    return {
        routesWidth,
        routesOpacity,
        innerRouteColors,
        outerRouteColors,
        networksWidth,
        networksOpacity,
        networksColor,
        networkSurfaceColors,
        networkSurfaceOpacity,
        networkSurfaceDashColor,
        networkSurfaceDashOpacity,
        networkSurfaceDashLength,
        networkSurfaceDashSpacing,
        networkGradeColors,
        networkGradeOpacity,
        networkGradeDashColor,
        networkGradeDashOpacity,
        networkGradeDashLength,
        networkGradeDashSpacing,
        dashOpacity,
        dashColor,
        dashLength,
        dashSpacing,
        wanderwegnetzGradeColors,
    };
}
export function setTestRouteStyles(styles) {
    const params = getURLSearchParams();
    params.set('summer_routes_width', styles.routesWidth.join(','));
    params.set('hike_route_opacity', String(styles.routesOpacity.wanderland));
    params.set('velo_route_opacity', String(styles.routesOpacity.veloland));
    params.set('mtb_route_opacity', String(styles.routesOpacity.mtbland));
    params.set('skating_route_opacity', String(styles.routesOpacity.skatingland));
    params.set('canoe_route_opacity', String(styles.routesOpacity.kanuland));
    params.set('hike_route_i_color', styles.innerRouteColors.wanderland);
    params.set('velo_route_i_color', styles.innerRouteColors.veloland);
    params.set('mtb_route_i_color', styles.innerRouteColors.mtbland);
    params.set('skating_route_i_color', styles.innerRouteColors.skatingland);
    params.set('canoe_route_i_color', styles.innerRouteColors.kanuland);
    params.set('velo_route_o_color', styles.outerRouteColors.veloland);
    params.set('mtb_route_o_color', styles.outerRouteColors.mtbland);
    params.set('skating_route_o_color', styles.outerRouteColors.skatingland);
    params.set('canoe_route_o_color', styles.outerRouteColors.kanuland);
    params.set('summer_network_width', styles.networksWidth.join(','));
    params.set('hike_net_opacity', String(styles.networksOpacity.wanderwegnetz));
    params.set('velo_net_opacity', String(styles.networksOpacity.velonetwork));
    params.set('mtb_net_opacity', String(styles.networksOpacity.mtbnetwork));
    params.set('skate_net_opacity', String(styles.networksOpacity.skatenetwork));
    params.set('hike_net_color', styles.networksColor.wanderwegnetz);
    params.set('velo_net_color', styles.networksColor.velonetwork);
    params.set('mtb_net_color', styles.networksColor.mtbnetwork);
    params.set('skate_net_color', styles.networksColor.skatenetwork);
    Object.entries(activityNetworkPrefixes).forEach(([layer, prefix]) => {
        const activityLayer = layer;
        params.set(`${prefix}_surf_paved`, styles.networkSurfaceColors[activityLayer].paved);
        params.set(`${prefix}_surf_natural`, styles.networkSurfaceColors[activityLayer].natural);
        params.set(`${prefix}_surf_opacity`, String(styles.networkSurfaceOpacity[activityLayer]));
        params.set(`${prefix}_surf_dash_color`, styles.networkSurfaceDashColor[activityLayer]);
        params.set(`${prefix}_surf_dash_opacity`, String(styles.networkSurfaceDashOpacity[activityLayer]));
        params.set(`${prefix}_surf_dash_len`, String(styles.networkSurfaceDashLength[activityLayer]));
        params.set(`${prefix}_surf_dash_gap`, String(styles.networkSurfaceDashSpacing[activityLayer]));
        params.set(`${prefix}_grade_1`, styles.networkGradeColors[activityLayer][1]);
        params.set(`${prefix}_grade_2`, styles.networkGradeColors[activityLayer][2]);
        params.set(`${prefix}_grade_3`, styles.networkGradeColors[activityLayer][3]);
        params.set(`${prefix}_grade_4`, styles.networkGradeColors[activityLayer][4]);
        params.set(`${prefix}_grade_5`, styles.networkGradeColors[activityLayer][5]);
        params.set(`${prefix}_grade_unknown`, styles.networkGradeColors[activityLayer].unknown);
        params.set(`${prefix}_grade_opacity`, String(styles.networkGradeOpacity[activityLayer]));
        params.set(`${prefix}_grade_dash_color`, styles.networkGradeDashColor[activityLayer]);
        params.set(`${prefix}_grade_dash_opacity`, String(styles.networkGradeDashOpacity[activityLayer]));
        params.set(`${prefix}_grade_dash_len`, String(styles.networkGradeDashLength[activityLayer]));
        params.set(`${prefix}_grade_dash_gap`, String(styles.networkGradeDashSpacing[activityLayer]));
    });
    params.set('hike_dash_opacity', String(styles.dashOpacity.wanderwegnetz));
    params.set('velo_dash_opacity', String(styles.dashOpacity.velonetwork));
    params.set('mtb_dash_opacity', String(styles.dashOpacity.mtbnetwork));
    params.set('skate_dash_opacity', String(styles.dashOpacity.skatenetwork));
    params.set('hike_dash_color', styles.dashColor.wanderwegnetz);
    params.set('velo_dash_color', styles.dashColor.velonetwork);
    params.set('mtb_dash_color', styles.dashColor.mtbnetwork);
    params.set('skate_dash_color', styles.dashColor.skatenetwork);
    params.set('hike_dash_len', String(styles.dashLength.wanderwegnetz));
    params.set('velo_dash_len', String(styles.dashLength.velonetwork));
    params.set('mtb_dash_len', String(styles.dashLength.mtbnetwork));
    params.set('skate_dash_len', String(styles.dashLength.skatenetwork));
    params.set('hike_dash_gap', String(styles.dashSpacing.wanderwegnetz));
    params.set('velo_dash_gap', String(styles.dashSpacing.velonetwork));
    params.set('mtb_dash_gap', String(styles.dashSpacing.mtbnetwork));
    params.set('skate_dash_gap', String(styles.dashSpacing.skatenetwork));
    params.set('hike_alpine_color', styles.wanderwegnetzGradeColors.alpine_trail);
    params.set('hike_mountain_color', styles.wanderwegnetzGradeColors.mountain_trail);
    params.set('hike_hiking_color', styles.wanderwegnetzGradeColors.hiking_trail);
    setURLSearchParams(params, true);
}
//# sourceMappingURL=urlSearchParams.js.map